import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MessageSquare, HelpCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSeo } from "@/lib/useSeo";
import { COMPANY } from "@/data/company";

const STATIC_FAQ = [
  { q: "Sous quel délai puis-je obtenir un devis ?", a: "Après une visite technique gratuite, nous vous transmettons un devis détaillé sous 24 à 48h." },
  { q: "Travaillez-vous pour les collectivités et les entreprises ?", a: "Oui. SMT Provence intervient pour les particuliers, les professionnels, les entreprises, les collectivités et les structures publiques." },
  { q: "Quelle est votre zone d'intervention ?", a: "Nous intervenons à Saint-Victoret et dans tout le département des Bouches-du-Rhône (Marignane, Vitrolles, Marseille, Aix-en-Provence…)." },
  { q: "Le déplacement et le devis sont-ils payants ?", a: "Non, le devis est personnalisé, gratuit et sans engagement." },
  { q: "Réalisez-vous le gros œuvre comme les finitions ?", a: "Oui, nous intervenons de la fondation aux finitions : maçonnerie, béton, murs, clôtures, VRD, reprises et enduits." },
  { q: "Vos ouvrages sont-ils conformes aux normes ?", a: "Tous nos ouvrages structurels sont réalisés dans le respect des règles de l'art et des normes en vigueur, pour garantir solidité et pérennité." },
];

export default function Faq() {
  useSeo("FAQ", "Questions fréquentes sur nos travaux de maçonnerie, béton, murs, clôtures et aménagements extérieurs.");
  const [faqArticles, setFaqArticles] = useState([]);
  useEffect(() => { api.get("/articles", { params: { type: "faq" } }).then((r) => setFaqArticles(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <section className="pt-32 pb-12 bg-ink">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white">Foire aux questions</h1>
          <p className="mt-4 text-white/70 max-w-2xl">Tout ce que vous devez savoir avant de lancer votre projet avec SMT Provence.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Left: contact card */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 bg-card border border-border rounded-2xl p-7">
            <div className="h-12 w-12 rounded-xl bg-accent text-primary flex items-center justify-center mb-4"><HelpCircle size={24} /></div>
            <h2 className="font-heading text-2xl font-black text-ink">Une question sans réponse ?</h2>
            <p className="mt-2 text-ink/60 text-sm leading-relaxed">Notre équipe vous répond directement et vous accompagne dans votre projet.</p>
            <div className="mt-5 space-y-3">
              <a href={`tel:${COMPANY.phoneHref}`} className="flex items-center gap-3 text-ink hover:text-primary transition"><span className="h-9 w-9 rounded-lg bg-accent text-primary flex items-center justify-center"><Phone size={17} /></span><span className="font-bold">{COMPANY.phone}</span></a>
              <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 text-ink hover:text-primary transition"><span className="h-9 w-9 rounded-lg bg-accent text-primary flex items-center justify-center"><Mail size={17} /></span><span className="font-semibold text-sm">{COMPANY.email}</span></a>
            </div>
            <Link to="/contact" className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-primary text-white px-6 py-3.5 rounded-full font-bold hover:brightness-95 transition"><MessageSquare size={18} /> Demander un devis</Link>
          </div>
        </aside>

        {/* Right: accordion */}
        <div className="lg:col-span-2">
          <Accordion type="single" collapsible className="space-y-3" data-testid="faq-accordion">
            {STATIC_FAQ.map((f, i) => (
              <AccordionItem key={i} value={`s${i}`} className="bg-card border border-border rounded-xl px-5 data-[state=open]:border-primary transition-colors">
                <AccordionTrigger className="text-left font-heading font-black text-ink hover:no-underline py-5">{f.q}</AccordionTrigger>
                <AccordionContent className="text-ink/70 leading-relaxed pb-5">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {faqArticles.map((art) => (
            <div key={art.id} className="mt-10">
              <h2 className="font-heading text-2xl font-black text-ink mb-4">{art.title}</h2>
              <div className="article-body bg-card border border-border rounded-xl p-6" dangerouslySetInnerHTML={{ __html: art.content }} />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
